/**
 * Pure filter engine for the V1 search-results experience.
 *
 * No React, no MobX. Takes raw data + filter state and returns a filtered array.
 * Easy to unit-test in isolation.
 *
 * Supports four filter shapes per-column:
 *   - { range: { min, max } }                     — numeric
 *   - { values: Set<string> }                     — controlled list (Situation, Country)
 *   - { text: string }                            — substring (City)
 *   - { keywords: Set<string>, mode: 'or'|'and' } — keyword cloud (Industry, Value chain, Products, End-markets)
 *
 * Plus a unified 'location' shape that carries both `values` (country) AND `text` (city)
 * in one object so the user opens one popover and applies one chip-set for Location.
 */

import { normalizeKeywordQuery } from '../../../../utils/keywordQuery';
import { SPOTLIGHT_DEFINITIONS } from './spotlightDefinitions';
import {
  COLUMN_FILTER_TYPES,
  COLUMN_FILTER_TYPE_BY_KEY,
  dataFieldForKey
} from './columnFilterTypes';

/**
 * Extract string names from a tag-array, tolerant of both shapes:
 *   - Array<{ name: string, ... }>  (real codebase)
 *   - Array<string>                 (prototype, tests)
 */
function tagToName(x) {
  if (x == null) return '';
  if (typeof x === 'string') return x;
  return x.name ?? '';
}

export function arrToNames(arr) {
  if (!Array.isArray(arr)) return [];
  return arr.map(tagToName).filter(Boolean);
}

/**
 * Coerce a value that should be numeric (but might be a string from the API,
 * or null, or NaN) into a finite Number — or return `null` if it can't be.
 * Used by every numeric comparison in this module so a stringly-typed `revenue`
 * field like "37.2" doesn't break sort / extent / threshold logic.
 */
function toNumberOrNull(value) {
  if (value == null) return null;
  // Empty / whitespace-only strings coerce to 0 via Number(""), so reject them
  // explicitly before falling into the generic numeric coercion.
  if (typeof value === 'string' && value.trim() === '') return null;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

/**
 * Compute the threshold value at quantile q (0..1) across a numeric field
 * over the full result set. Excludes nulls / NaN.
 *
 * Static against `results` (the loaded set), NOT against the filtered subset —
 * matches the V1 prototype, so "Top quartile" is a stable, meaningful concept
 * that doesn't shift as the user narrows the filtered view.
 */
export function getQuartile(results, field, q) {
  const dataField = dataFieldForKey(field);
  const vals = [];
  for (const c of results) {
    const v = toNumberOrNull(c?.[dataField] ?? c?.[field]);
    if (v !== null) vals.push(v);
  }
  if (!vals.length) return null;
  vals.sort((a, b) => a - b);
  const idx = Math.min(vals.length - 1, Math.max(0, Math.floor(vals.length * q)));
  return vals[idx];
}

/**
 * Pre-compute every threshold a spotlight predicate might need
 * for the current `results` set. Pass the returned `ctx` to predicates.
 */
export function buildSpotlightContext(results) {
  return {
    quartiles: {
      ebitPercentage_p75: getQuartile(results, 'ebitPercentage', 0.75)
    }
  };
}

// ---------------------------------------------------------------------------
// Per-column-filter matchers
// ---------------------------------------------------------------------------

function matchNumeric(company, key, range) {
  if (!range) return true;
  // Prefer the underscore-prefixed numeric copy of the field — the display
  // column (`cagr3Y`, `ebitPercentage`, ...) is a string like "8.3%". Fall
  // back to the non-prefixed field when the numeric copy is absent (e.g.
  // for `employees` / `revenue` which are sometimes plain numbers, and for
  // unit tests that use simple fixtures).
  const dataField = dataFieldForKey(key);
  const v = toNumberOrNull(company?.[dataField] ?? company?.[key]);
  if (v === null) return false; // filtering on numeric excludes n/a (incl. unparseable strings)
  if (range.min != null && v < range.min) return false;
  if (range.max != null && v > range.max) return false;
  return true;
}

function matchControlledTag(company, key, values) {
  // For situation / value-chain-like fields where data is Array<{name}>, match if any
  // tag-name is in `values`. Tolerates Array<string> too.
  //
  // V1.4 — case-insensitive matching. The popover collects values from the
  // data via `arrToNames` and the engine reads via the same helper, so in
  // theory exact matching would suffice. But the user reported the
  // "Loss-making" / "declining" Situation values not narrowing the result
  // set, which suggests at least one casing variant lives in the data
  // somewhere (e.g. "loss-making" vs "Loss-making" vs "Loss Making"). The
  // popover dedupes by exact string so the user may select one variant
  // while a different-cased row exists. Folding to lower-case at compare
  // time makes the filter robust against that without affecting other
  // semantics — selecting "Healthy" still only matches "healthy" /
  // "Healthy" / "HEALTHY", never "Distressed".
  if (!values?.size) return true;
  const names = arrToNames(company?.[key]);
  if (!names.length) return false;
  const valuesLower = new Set();
  for (const v of values) valuesLower.add(typeof v === 'string' ? v.toLowerCase() : v);
  for (const n of names) {
    if (valuesLower.has(typeof n === 'string' ? n.toLowerCase() : n)) return true;
  }
  return false;
}

function matchKeywordCloud(company, key, filter) {
  if (!filter?.keywords?.size) return true;
  const tags = arrToNames(company?.[key]).map((n) => n.toLowerCase());
  if (!tags.length) return false;

  const queries = [...filter.keywords].map((q) => q.toLowerCase());
  const mode = filter.mode || 'or';

  const matchOne = (q) => {
    // "quoted" → exact equality
    if (q.length >= 2 && q.startsWith('"') && q.endsWith('"')) {
      const exact = q.slice(1, -1);
      return tags.includes(exact);
    }
    // bare → substring (case-insensitive, since we lowercased both)
    return tags.some((t) => t.includes(q));
  };

  if (mode === 'and') return queries.every(matchOne);
  return queries.some(matchOne);
}

function matchLocation(company, filter) {
  // Location filter carries:
  //   - values?: Set<countryName> for country
  //   - text?:   string substring for city
  // The `headquarters` field is "City, Country" — split on the last comma.
  if (!filter) return true;
  const hq = company?.headquarters ?? '';
  const splitIdx = hq.lastIndexOf(',');
  const city = (splitIdx >= 0 ? hq.slice(0, splitIdx) : hq).trim();
  const country = (splitIdx >= 0 ? hq.slice(splitIdx + 1) : '').trim();

  if (filter.values?.size && !filter.values.has(country)) return false;
  if (filter.text && !city.toLowerCase().includes(filter.text.toLowerCase())) return false;
  return true;
}

// ---------------------------------------------------------------------------
// Main filter pass
// ---------------------------------------------------------------------------

/**
 * Apply the full filter cascade to `results`.
 *
 * @param {Array}  results          The loaded result set (non-excluded already filtered upstream).
 * @param {Set}    activeSpotlights Set of spotlight IDs currently active.
 * @param {Object} columnFilters    Map: columnKey → filter shape.
 * @param {Object} ctx              Pre-built spotlight context (from buildSpotlightContext).
 * @param {Array}  [spotlightDefs]  Override the default spotlight definitions (testing).
 * @returns {Array} filtered results
 */
// Helper: does this row pass every currently-active spotlight? Extracted to
// keep `applyExploreFilters` under Sonar's CC-15 budget. AND-semantics across
// active spotlights — a single failure short-circuits.
function _passesActiveSpotlights(company, defs, spots, ctx, results) {
  for (const def of defs) {
    if (spots.has(def.id) && !def.match(company, ctx, results)) return false;
  }
  return true;
}

// Helper: does this row pass a single column filter? Dispatches by the
// per-column filter type. Pulled out so `applyExploreFilters` doesn't carry
// the full per-type dispatch table inline.
function _passesColumnFilter(company, key, filter) {
  // Location is the single combined column with two sub-filters.
  if (key === 'headquarters' || key === 'location') {
    return matchLocation(company, filter);
  }
  const typeId = COLUMN_FILTER_TYPE_BY_KEY[key];
  if (typeId === COLUMN_FILTER_TYPES.NUMERIC) return matchNumeric(company, key, filter.range);
  if (typeId === COLUMN_FILTER_TYPES.CONTROLLED)
    return matchControlledTag(company, key, filter.values);
  if (typeId === COLUMN_FILTER_TYPES.KEYWORD_CLOUD) return matchKeywordCloud(company, key, filter);
  if (filter.text) {
    // Fallback substring on string fields.
    const v = String(company?.[key] ?? '').toLowerCase();
    return v.includes(filter.text.toLowerCase());
  }
  return true;
}

// Helper: does this row pass every active column filter? AND-semantics across
// columns — a single failure short-circuits.
function _passesAllColumnFilters(company, cols) {
  for (const key of Object.keys(cols)) {
    const filter = cols[key];
    if (!filter) continue;
    if (!_passesColumnFilter(company, key, filter)) return false;
  }
  return true;
}

export function applyExploreFilters(results, activeSpotlights, columnFilters, ctx, spotlightDefs) {
  const defs = spotlightDefs || SPOTLIGHT_DEFINITIONS;
  const cols = columnFilters || {};
  const spots = activeSpotlights || new Set();

  return results.filter(
    (c) => _passesActiveSpotlights(c, defs, spots, ctx, results) && _passesAllColumnFilters(c, cols)
  );
}

/**
 * For each spotlight, compute "how many companies would match if THIS spotlight
 * were active (in addition to all currently-active column filters, and ignoring
 * other spotlights)". Used to render honest live counts on every chip.
 *
 * Logic mirrors the V1 prototype's `applyFiltersExceptSpotlights() ∩ thisSpotlight`.
 */
export function getSpotlightCounts(results, columnFilters, spotlightDefs) {
  const defs = spotlightDefs || SPOTLIGHT_DEFINITIONS;
  const ctx = buildSpotlightContext(results);
  // Base = results with column filters applied, no spotlights
  const base = applyExploreFilters(results, new Set(), columnFilters, ctx, defs);
  const counts = {};
  for (const def of defs) {
    let n = 0;
    for (const c of base) {
      if (def.match(c, ctx, results)) n += 1;
    }
    counts[def.id] = n;
  }
  return counts;
}

/**
 * Accumulate one company's tags into the needle → { companyCount, labelFreq } map.
 * Company-once per needle; labelFreq counts raw casing occurrences.
 */
function accumulateTagFrequencies(byNeedle, names) {
  const seenNeedles = new Set();
  for (const n of names) {
    const { needle } = normalizeKeywordQuery(n);
    if (!needle) continue;
    let entry = byNeedle.get(needle);
    if (!entry) {
      entry = { companyCount: 0, labelFreq: new Map() };
      byNeedle.set(needle, entry);
    }
    entry.labelFreq.set(n, (entry.labelFreq.get(n) || 0) + 1);
    if (!seenNeedles.has(needle)) {
      seenNeedles.add(needle);
      entry.companyCount += 1;
    }
  }
}

/**
 * Pick the most frequent casing label from a labelFreq map; first-seen on ties.
 */
function bestLabelFromFreq(labelFreq) {
  let bestLabel = null;
  let bestFreq = -1;
  for (const [label, freq] of labelFreq) {
    if (freq > bestFreq) {
      bestFreq = freq;
      bestLabel = label;
    }
  }
  return bestLabel;
}

/**
 * Frequency-rank the distinct tag-names appearing in `arrField` across `results`.
 * Returns an array of [name, count] sorted by count desc, used by KeywordCloudFilter
 * to power the "top-10 in current set" list.
 *
 * TD-2626 (B): fold casing variants into one row. Selection already keys on
 * `normalizeKeywordQuery`, so listing raw casings as separate checkboxes made
 * click-to-uncheck delete the whole filter when a second visual row shared the
 * same needle. Count each company once per normalised value; pick the most
 * frequent casing as the display label (first-seen casing on a frequency tie).
 */
export function rankTagFrequencies(results, arrField) {
  const byNeedle = new Map();
  for (const c of results) {
    accumulateTagFrequencies(byNeedle, arrToNames(c?.[arrField]));
  }
  const ranked = [];
  for (const entry of byNeedle.values()) {
    ranked.push([bestLabelFromFreq(entry.labelFreq), entry.companyCount]);
  }
  return ranked.sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
}

/**
 * Match a single keyword query against a pre-lowercased tag list (same rules as
 * matchKeywordCloud: quoted → exact, bare → substring).
 */
function matchLowercasedTags(tags, rawQuery) {
  if (!tags.length) return false;
  const q = String(rawQuery).toLowerCase();
  if (q.length >= 2 && q.startsWith('"') && q.endsWith('"')) {
    return tags.includes(q.slice(1, -1));
  }
  return tags.some((t) => t.includes(q));
}

/** Build a Set of normalised needles from a keywords Set. */
function needlesFromKeywords(selectedKeywords) {
  const needles = new Set();
  if (!selectedKeywords?.size) return needles;
  for (const raw of selectedKeywords) {
    const { needle } = normalizeKeywordQuery(raw);
    if (needle) needles.add(needle);
  }
  return needles;
}

/** Stable fingerprint of column filters excluding `columnKey` (except-self base). */
export function fingerprintExceptSelfFilters(columnFilters, columnKey) {
  const parts = [];
  for (const key of Object.keys(columnFilters || {}).sort()) {
    if (key === columnKey) continue;
    const v = columnFilters[key];
    if (v == null) continue;
    if (v.keywords instanceof Set) {
      parts.push(`${key}:kw:${[...v.keywords].sort().join('\u0001')}:${v.mode || 'or'}`);
      continue;
    }
    if (v.range) {
      parts.push(`${key}:rg:${v.range.min ?? ''}:${v.range.max ?? ''}`);
      continue;
    }
    if (v.values instanceof Set) {
      parts.push(`${key}:vs:${[...v.values].sort().join('\u0001')}`);
      continue;
    }
    parts.push(`${key}:${JSON.stringify(v)}`);
  }
  return parts.join('|');
}

function bitMaskWords(n) {
  return (n + 31) >>> 5;
}

function setBit(mask, i) {
  mask[i >>> 5] |= 1 << (i & 31);
}

function popcountMask(mask) {
  let n = 0;
  for (let i = 0; i < mask.length; i++) {
    let v = mask[i];
    while (v) {
      v &= v - 1;
      n += 1;
    }
  }
  return n;
}

function copyMask(dst, src) {
  dst.set(src);
}

function orInto(dst, src) {
  for (let i = 0; i < dst.length; i++) dst[i] |= src[i];
}

function andInto(dst, src) {
  for (let i = 0; i < dst.length; i++) dst[i] &= src[i];
}

/**
 * OR every unique-tag mask whose tag matches `needle` (quoted → exact, bare →
 * substring) into `out`. Used by the inverted keyword-cloud index build so we
 * walk unique tags once per candidate instead of companies × candidates.
 */
function orMatchingTagMasksInto(out, tagMasks, needle, quoted) {
  if (!needle) return;
  if (quoted) {
    const exact = tagMasks.get(needle);
    if (exact) orInto(out, exact);
    return;
  }
  const exact = tagMasks.get(needle);
  if (exact) orInto(out, exact);
  for (const [tag, tagMask] of tagMasks) {
    // Skip the exact key (already OR'd) and tags too short to contain needle.
    if (tag === needle || tag.length < needle.length) continue;
    if (tag.includes(needle)) orInto(out, tagMask);
  }
}

/**
 * TD-2626 (C) perf: build per-value match bitmasks once for the except-self base.
 * Toggle/mode changes then only combine masks (see countKeywordCloudFromIndex).
 *
 * Build walks unique tags (not companies × candidates): company → tag masks,
 * then each candidate ORs matching tag masks. Same substring/exact semantics.
 *
 * @returns {{ labels: string[], needles: string[], masks: Uint32Array[], baseLen: number, words: number }}
 */
export function buildKeywordCloudMatchIndex({
  results,
  columnFilters = {},
  columnKey,
  activeSpotlights = new Set()
}) {
  const field = dataFieldForKey(columnKey);
  const ctx = buildSpotlightContext(results);
  const otherFilters = { ...columnFilters };
  delete otherFilters[columnKey];
  const spots =
    activeSpotlights instanceof Set ? activeSpotlights : new Set(activeSpotlights || []);
  const base = applyExploreFilters(results, spots, otherFilters, ctx);
  const baseTagLists = base.map((c) => arrToNames(c?.[field]).map((n) => n.toLowerCase()));
  const baseLen = baseTagLists.length;
  const words = bitMaskWords(baseLen);

  // Browse list from full result set — selection does not rebuild this index.
  const candidates = rankTagFrequencies(results, field);

  // Inverted: unique lowercased tag → companies that carry it.
  const tagMasks = new Map();
  for (let i = 0; i < baseLen; i++) {
    const tags = baseTagLists[i];
    for (const tag of tags) {
      let mask = tagMasks.get(tag);
      if (!mask) {
        mask = new Uint32Array(words);
        tagMasks.set(tag, mask);
      }
      setBit(mask, i);
    }
  }

  const labels = [];
  const needles = [];
  const masks = [];
  for (const [label] of candidates) {
    const { needle, quoted } = normalizeKeywordQuery(label);
    labels.push(label);
    needles.push(needle);
    const mask = new Uint32Array(words);
    orMatchingTagMasksInto(mask, tagMasks, needle, quoted);
    masks.push(mask);
  }

  return { labels, needles, masks, baseLen, words, baseTagLists };
}

/** Lazily add free-text selections that were not in the ranked candidate list. */
function ensureSelectedMasks(index, selectedKeywords) {
  if (!selectedKeywords?.size) return;
  const { baseTagLists, baseLen, words, labels, needles, masks } = index;
  for (const raw of selectedKeywords) {
    const { needle } = normalizeKeywordQuery(raw);
    if (!needle || needles.includes(needle)) continue;
    const label = String(raw);
    const mask = new Uint32Array(words);
    for (let i = 0; i < baseLen; i++) {
      if (matchLowercasedTags(baseTagLists[i], label)) setBit(mask, i);
    }
    labels.push(label);
    needles.push(needle);
    masks.push(mask);
  }
}

/**
 * Derive predictive counts from a precomputed match index under Match mode.
 * With 0–1 selected, counts are value-alone (popcount of each mask). With 2+,
 * OR/AND combines selected masks with each candidate.
 */
export function countKeywordCloudFromIndex(index, selectedKeywords, mode = 'or') {
  if (!index) return [];
  ensureSelectedMasks(index, selectedKeywords);
  const { labels, needles, masks, words } = index;
  const matchMode = mode === 'and' ? 'and' : 'or';
  const selectedNeedles = needlesFromKeywords(selectedKeywords);
  const selectedIdx = [];
  for (let i = 0; i < needles.length; i++) {
    if (selectedNeedles.has(needles[i])) selectedIdx.push(i);
  }

  const scratch = new Uint32Array(words);
  const out = [];

  if (selectedIdx.length < 2) {
    for (let i = 0; i < labels.length; i++) {
      const count = popcountMask(masks[i]);
      if (count > 0 || selectedNeedles.has(needles[i])) out.push([labels[i], count]);
    }
  } else {
    copyMask(scratch, masks[selectedIdx[0]]);
    for (let s = 1; s < selectedIdx.length; s++) {
      if (matchMode === 'and') andInto(scratch, masks[selectedIdx[s]]);
      else orInto(scratch, masks[selectedIdx[s]]);
    }
    const selectedCombined = popcountMask(scratch);
    const selectedOnly = new Uint32Array(scratch);

    for (let i = 0; i < labels.length; i++) {
      let count;
      if (selectedNeedles.has(needles[i])) {
        count = selectedCombined;
      } else {
        copyMask(scratch, selectedOnly);
        if (matchMode === 'and') andInto(scratch, masks[i]);
        else orInto(scratch, masks[i]);
        count = popcountMask(scratch);
      }
      if (count > 0 || selectedNeedles.has(needles[i])) out.push([labels[i], count]);
    }
  }

  return out.sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
}

/**
 * TD-2626 (C): predictive keyword-value counts for the cloud editor.
 *
 * Prefer `buildKeywordCloudMatchIndex` + `countKeywordCloudFromIndex` in the UI
 * so the O(candidates × companies) mask build runs only when the except-self
 * base changes — not on every checkbox toggle.
 *
 * @param {object} args
 * @param {Array} args.results
 * @param {object} [args.columnFilters]
 * @param {string} args.columnKey
 * @param {Set} [args.activeSpotlights]
 * @param {Set} [args.selectedKeywords]
 * @param {'or'|'and'} [args.mode]
 * @returns {Array<[string, number]>}
 */
export function getKeywordCloudValueCounts({
  results,
  columnFilters = {},
  columnKey,
  activeSpotlights = new Set(),
  selectedKeywords,
  mode = 'or'
}) {
  const index = buildKeywordCloudMatchIndex({
    results,
    columnFilters,
    columnKey,
    activeSpotlights
  });
  return countKeywordCloudFromIndex(index, selectedKeywords, mode);
}

/**
 * Frequency-rank distinct single-value strings (e.g. country names parsed from
 * headquarters). Used by LocationFilter for the Country checkbox list.
 */
export function rankCountries(results) {
  const counts = new Map();
  for (const c of results) {
    const hq = c?.headquarters ?? '';
    const idx = hq.lastIndexOf(',');
    const country = (idx >= 0 ? hq.slice(idx + 1) : '').trim();
    if (!country) continue;
    counts.set(country, (counts.get(country) || 0) + 1);
  }
  return [...counts.entries()].sort((a, b) => b[1] - a[1]);
}

/**
 * Compute the min and max value of a numeric field across `results`. Used by
 * NumericRangeFilter to show "Range in current set: X to Y".
 */
export function getNumericExtent(results, field) {
  const dataField = dataFieldForKey(field);
  let lo = null;
  let hi = null;
  for (const c of results) {
    const v = toNumberOrNull(c?.[dataField] ?? c?.[field]);
    if (v === null) continue;
    if (lo == null || v < lo) lo = v;
    if (hi == null || v > hi) hi = v;
  }
  return { min: lo, max: hi };
}

/**
 * True if `columnFilters` and `activeSpotlights` have anything to filter on.
 * Used to gate render of the chip rail.
 */
// Per-filter activity check — pulled out so `hasAnyActiveFilter` stays under
// Sonar's CC-15 budget. Returns true if this single column filter has any
// non-empty sub-condition (range, values, text, keywords).
function _columnFilterHasActiveValue(f) {
  if (!f) return false;
  if (f.range && (f.range.min != null || f.range.max != null)) return true;
  if (f.values?.size) return true;
  if (f.text) return true;
  if (f.keywords?.size) return true;
  return false;
}

export function hasAnyActiveFilter(activeSpotlights, columnFilters) {
  if (activeSpotlights?.size > 0) return true;
  if (!columnFilters) return false;
  for (const key of Object.keys(columnFilters)) {
    if (_columnFilterHasActiveValue(columnFilters[key])) return true;
  }
  return false;
}

/**
 * Like `hasAnyActiveFilter`, but first drops spotlights that are hidden for
 * the current search category (`categoryExcludedSpotlightIds`). A stale /
 * inherited hidden spotlight id (e.g. 'high-match' on an Industries search)
 * must NOT read as "a filter is active" — otherwise the bulk-exclude control
 * would offer to act while nothing actually narrows the list. Gates the
 * TD-2461 "Bulk exclude" dropdown.
 */
export function hasEffectiveActiveFilter(
  activeSpotlights,
  columnFilters,
  categoryExcludedSpotlightIds
) {
  let effectiveSpotlights = activeSpotlights;
  if (categoryExcludedSpotlightIds && activeSpotlights?.size) {
    effectiveSpotlights = new Set(
      [...activeSpotlights].filter((id) => !categoryExcludedSpotlightIds.has(id))
    );
  }
  return hasAnyActiveFilter(effectiveSpotlights, columnFilters);
}

/**
 * Set-difference by id: every row in `base` that is NOT in `filtered`.
 * `filtered` is a subset of `base` (both derive from the same result set via
 * `applyExploreFilters`), so this yields "the rows the active filters excluded"
 * — the target set for the TD-2461 "Keep matches, exclude the rest" direction.
 * F ⊆ A ⇒ A\F; empty F ⇒ all of base; F === A ⇒ [].
 */
export function computeComplement(base, filtered, idKey = 'id') {
  const keepIds = new Set((filtered || []).map((c) => c?.[idKey]));
  return (base || []).filter((c) => !keepIds.has(c?.[idKey]));
}

/**
 * Enablement guards for the two bulk-exclusion directions, kept pure so the
 * invariant is testable without standing up the whole search-results store.
 *
 * Both require an effective filter, a non-empty target set, and no exclusion
 * already in flight.
 *
 * `canKeepMatchesSet` additionally requires a non-empty set to KEEP. Without
 * that, a filter matching nothing makes the complement the entire list (empty F
 * ⇒ all of base, per `computeComplement`) and "Keep matches, exclude the rest"
 * degenerates into "exclude everything" — reachable from any zero-match filter
 * and, unavoidably, straight after a successful "Remove filtered", which empties
 * the matching set by construction.
 */
export function canRemoveFilteredSet({ hasEffectiveFilter, keptCount, isBulkExcluding }) {
  return !!hasEffectiveFilter && keptCount > 0 && !isBulkExcluding;
}

export function canKeepMatchesSet({
  hasEffectiveFilter,
  keptCount,
  complementCount,
  isBulkExcluding
}) {
  return !!hasEffectiveFilter && keptCount > 0 && complementCount > 0 && !isBulkExcluding;
}
